import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  Field,
  FieldError,
  FieldLabel,
  Textarea,
  toast,
} from "@/components/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { useParams, useSearch } from "@tanstack/react-router";
import { Controller, useForm } from "react-hook-form";
import type { z } from "zod";
import { useCreateAmazonAdminNote } from "../network/useCreateAmazonAdminNote";
import { useCreateCuraiAdminNote } from "../network/useCreateCuraiAdminNote";
import { adminNoteSchema } from "./schema";

interface AdminNoteModalProps {
  open: boolean;
  onOpenChange(o: boolean): void;
  isAmazon?: boolean;
}

export function AdminNoteModal({
  open,
  onOpenChange,
  isAmazon = false,
}: AdminNoteModalProps) {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const { encounterId } = useSearch({ from: "/patient/$patientId" });

  const form = useForm<z.infer<typeof adminNoteSchema>>({
    resolver: zodResolver(adminNoteSchema),
    defaultValues: {
      text: "",
    },
  });

  const createAmazonAdminNote = useCreateAmazonAdminNote({
    encounterId: encounterId as string,
    patientId,
  });

  const createCuraiAdminNote = useCreateCuraiAdminNote({
    encounterId: encounterId as string,
    patientId,
  });
  const noteMutation = isAmazon ? createAmazonAdminNote : createCuraiAdminNote;

  const onSubmit = async (values: z.infer<typeof adminNoteSchema>) => {
    try {
      await noteMutation.mutateAsync({ noteText: values.text });
      toast.success("Admin note created successfully");
      form.reset();
      onOpenChange(false);
    } catch (error) {
      toast.error("Failed to create admin note. Please try again.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogContent className="w-full max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Admin Note</DialogTitle>
            <DialogDescription className="hidden">
              admin note modal
            </DialogDescription>
          </DialogHeader>

          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4 pr-3"
          >
            <Controller
              control={form.control}
              name="text"
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel htmlFor="text">Note</FieldLabel>
                  <Textarea
                    {...field}
                    id="text"
                    rows={20}
                    aria-invalid={fieldState.invalid}
                  />
                  <FieldError errors={[fieldState.error]} />
                </Field>
              )}
            />
            <Button
              size="sm"
              type="submit"
              className="w-fit ml-auto"
              isLoading={noteMutation.isPending}
            >
              Save
            </Button>
          </form>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
